import React, { useState, useEffect, useCallback } from "react";
import { api } from "../api";
import Switch from "../crm/automations/Switch";
import { pushSupported as supported, subscriptionFor } from "../pushRepair";

/*
 * Settings → Phone notifications. Turns on Web Push for THIS device, and below
 * it one switch per alert kind (src/push KINDS). The switches are server-wide:
 * every device that is on hears the same alerts.
 *
 * On Android, Chrome receives push straight from the browser tab; installing it
 * to the home screen is only a nicety, so the card suggests it rather than
 * requiring it. On an iPhone this works only from the home-screen app — Safari
 * in a tab has no push at all — so the card says so instead of offering a
 * button that cannot work.
 */

function isIos() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

function isAndroid() {
  return /android/i.test(navigator.userAgent);
}

function isStandalone() {
  return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
}

async function currentSubscription() {
  const reg = await navigator.serviceWorker.getRegistration("/");
  return reg ? reg.pushManager.getSubscription() : null;
}

export default function PhoneNotifications() {
  const [status, setStatus] = useState(null);
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [pendingKind, setPendingKind] = useState(null);

  const refresh = useCallback(async () => {
    try {
      setStatus(await api("/push/status"));
      if (supported()) setSubscribed(!!(await currentSubscription()));
    } catch (err) {
      setError(err.message);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const turnOn = async () => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      // Must be asked from the tap itself — iOS refuses a prompt that is not.
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        throw new Error(
          isIos()
            ? "Notifications are blocked for this app. Allow them in the iPhone's Settings, then try again."
            : "Notifications are blocked for this site. In Chrome, tap the icon left of the address → Permissions → Notifications → Allow, then try again."
        );
      }
      const reg = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
      await navigator.serviceWorker.ready;
      // A subscription made with an old server key is replaced, not reused.
      const { subscription } = await subscriptionFor(reg, status.public_key);
      await api("/push/subscribe", { method: "POST", body: JSON.stringify({ subscription: subscription.toJSON() }) });
      setSubscribed(true);
      setMessage("On. This device will get the alerts switched on above.");
      refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const turnOff = async () => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const subscription = await currentSubscription();
      if (subscription) {
        await api("/push/unsubscribe", { method: "POST", body: JSON.stringify({ endpoint: subscription.endpoint }) });
        await subscription.unsubscribe();
      }
      setSubscribed(false);
      setMessage("Off for this device.");
      refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  // The switch shows what it is asking for and stays disabled until the server
  // answers, so a failed save snaps back rather than claiming a setting it lost.
  const setKind = async (kind, on) => {
    setPendingKind(kind);
    setError(null);
    setStatus((s) => ({ ...s, prefs: { ...s.prefs, [kind]: on } }));
    try {
      const result = await api("/push/prefs", { method: "PUT", body: JSON.stringify({ prefs: { [kind]: on } }) });
      setStatus((s) => ({ ...s, prefs: result.prefs }));
    } catch (err) {
      setStatus((s) => ({ ...s, prefs: { ...s.prefs, [kind]: !on } }));
      setError(err.message);
    } finally {
      setPendingKind(null);
    }
  };

  const sendTest = async () => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const result = await api("/push/test", { method: "POST" });
      setMessage(`Test sent to ${result.delivered} device${result.delivered === 1 ? "" : "s"}.`);
      refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  // Alerts the CRM sent that reached no phone, since the last one that did.
  const health = status && status.configured ? status.health : null;
  let warning = null;
  if (health && (health.missed > 0 || health.failing_devices > 0)) {
    const since = health.since
      ? new Date(health.since).toLocaleDateString(undefined, { month: "short", day: "numeric" })
      : null;
    warning = (
      <div className="gmail-sync-error" style={{ marginBottom: 14 }}>
        {health.missed > 0 ? (
          <>
            <b>
              {health.missed} alert{health.missed === 1 ? "" : "s"}
              {since ? ` since ${since}` : ""} reached no phone.
            </b>{" "}
          </>
        ) : (
          <b>Alerts are failing on {health.failing_devices === 1 ? "a device" : `${health.failing_devices} devices`}. </b>
        )}
        Open the CRM on your phone and it reconnects itself; if this stays, tap Turn off and Turn on there, then
        Send a test.
        {health.last_error && <div style={{ marginTop: 4, opacity: 0.8 }}>Last error: {health.last_error}</div>}
      </div>
    );
  }

  let body;
  if (!status) {
    body = <p className="toggle-desc">Loading…</p>;
  } else if (!status.configured) {
    body = (
      <p className="toggle-desc">
        Not set up on the server yet: VAPID_PUBLIC_KEY and VAPID_PRIVATE_KEY need to be set on Railway.
      </p>
    );
  } else if (isIos() && !isStandalone()) {
    body = (
      <p className="toggle-desc">
        On iPhone, notifications only work from the home-screen app. In Safari, tap Share → Add to Home Screen,
        open the CRM from that icon, and come back to this page.
      </p>
    );
  } else if (!supported()) {
    body = <p className="toggle-desc">This browser can't receive notifications.</p>;
  } else {
    body = (
      <div className="gmail-actions">
        {subscribed ? (
          <>
            <button className="gmail-sync-btn" onClick={sendTest} disabled={busy}>
              Send a test
            </button>
            <button className="gmail-disconnect-btn" onClick={turnOff} disabled={busy}>
              Turn off on this device
            </button>
          </>
        ) : (
          <button className="gmail-connect-btn" onClick={turnOn} disabled={busy}>
            {busy ? "Turning on…" : "Turn on for this device"}
          </button>
        )}
      </div>
    );
  }

  // Android works from the tab; installed, a tapped alert opens the CRM as its
  // own app instead of a Chrome tab.
  const androidTip = status && status.configured && isAndroid() && !isStandalone() && supported();

  return (
    <div className="gmail-section">
      <h3>Phone notifications</h3>
      <p className="toggle-desc">
        Only the alerts switched on below reach your phone, at most {status ? status.daily_cap : 10} of each a day.
        Everything else stays in the CRM.
      </p>
      {warning}
      {status && status.configured && Array.isArray(status.kinds) && (
        <div style={{ marginBottom: 14 }}>
          {status.kinds.map((k) => (
            <div key={k.kind} className="crm-set__rowmain crm-set__rowmain--switch" style={{ padding: "8px 0" }}>
              <div className="crm-set__text">
                <b>{k.label}</b>
                <span>{k.describe}</span>
              </div>
              <Switch
                on={!!(status.prefs && status.prefs[k.kind])}
                pending={pendingKind === k.kind}
                label={k.label}
                onChange={(on) => setKind(k.kind, on)}
              />
            </div>
          ))}
        </div>
      )}
      {body}
      {androidTip && (
        <p className="toggle-desc" style={{ marginTop: 12, marginBottom: 0 }}>
          Tip: Chrome menu (⋮) → Add to Home screen, so tapping an alert opens the CRM as its own app.
        </p>
      )}
      {status && status.configured && (
        <p className={health && health.failing_devices > 0 ? "gmail-sync-error" : "gmail-sync-result"}>
          {status.devices} device{status.devices === 1 ? "" : "s"} signed up for alerts
          {health && health.failing_devices > 0 ? ` · ${health.failing_devices} failing` : ""}
          {subscribed ? " · this one included" : ""}
        </p>
      )}
      {message && <p className="gmail-sync-result">{message}</p>}
      {error && <p className="gmail-sync-error">{error}</p>}
    </div>
  );
}
